// Import dependencies
import { unsafeCSS, css, html, render, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { getIntegration, getTemplate } from '@automator/features/page-template-library/utilities';

// Import global utils
import { getURLParameter } from '@automator/shared/utils/get-url-parameter';
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-library-template-card' )
export class AutomatorLibraryTemplateCard extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The template ID
	 *
	 * @type {Number} The ID of the template
	 */
	@property( { type: Number, attribute: 'template-id' } ) accessor templateID;

	/**
	 * Create a reference to the dialog that doesn't exist yet
	 *
	 * @type {Object} The dialog reference
	 */
	$dialog = createRef();

	/**
	 * Checks whether the dialog should be shown automatically, based on the URL parameters
	 */
	connectedCallback() {
		super.connectedCallback();

		// Open the dialog automatically on page load
		this.handleOpenAutomatically();
	}

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|String|undefined} The template
	 */
	render() {
		// Check if the ID is defined
		if ( isEmpty( this.templateID ) ) {
			return;
		}

		// Check if the template data exists
		if ( isEmpty( this.directoryData ) ) {
			return sprintf(
				escapeHTML( __( 'Template ID %1$s not found', 'uncanny-automator' ) ),
				this.templateID
			);
		}

		return html`
			<div 
				class="template"
				@click=${ this.handleOpen }
			>
				${ this._templateHeading }
				${ this._templateRequirements }
			</div>
		`;
	}

	/**
	 * Gets the heading for the template.
	 *
	 * @return {TemplateResult} The heading element.
	 */
	get _templateHeading() {
		return html`
			<div class="template-heading">
				${ this.directoryData.title } 
				${ this._templatePremiumBadge }
			</div>
		`;
	}

	/**
	 * Gets the pro badge for the template.
	 *
	 * @return {TemplateResult|undefined} The pro badge element.
	 */
	get _templatePremiumBadge() {
		// Check if it requires Pro
		if ( this.directoryData.requires_pro ) {
			return html`
				<uo-chip 
					size="small"
					color="success"
					filled
				>
					${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
				</uo-chip>
			`;
		}

		// Check if it requires Elite
		if ( this.directoryData.requires_elite ) {
			return html`
				<uo-chip 
					size="small"
					color="success"
					filled
				>
					${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
				</uo-chip>
			`;
		}
	}

	/**
	 * Gets the requirements section for the template.
	 *
	 * @return {TemplateResult} The requirements section.
	 */
	get _templateRequirements() {
		// Show the requirements, except for Automator Pro and Automator Elite
		// Automator Pro = -10
		// Automator Elite = -20
		const requirements = this.directoryData.requirements.filter(
			integrationID =>
				! [ -10, -20 ].includes( integrationID )
		);

		return html`
			<div class="template-requirements">
				<div class="template-requirements-heading">
					${ escapeHTML( __( 'Requires', 'uncanny-automator' ) ) }
				</div>

				<div class="template-requirements-items">
					${ repeat(
						requirements,
						integrationID => integrationID,
						integrationID => {
							return this._templateRequirement( integrationID );
						}
					) }
				</div>
			</div>
		`;
	}

	/**
	 * Generates a template requirement element.
	 *
	 * @param {Number} integrationID - The integration ID (from automatorplugin.com)
	 * @return {TemplateResult|undefined} The requirement element.
	 */
	_templateRequirement( integrationID ) {
		// Get the integration
		const integration = getIntegration( 'id', integrationID );

		// Check if the integration is empty
		if ( isEmpty( integration ) ) {
			return;
		}

		return html`
			<div class="template-requirement">
				<div class="template-requirement-icon">
					<uo-icon integration="${ integration?.code }"></uo-icon>
				</div>

				${ integration?.name }
			</div>
		`;
	}

	/**
	 * Creates the dialog that shows the details
	 */
	createDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					size="xlarge"
					open

					no-header
					no-footer
					no-padding

					${ ref( this.$dialog ) }

					@uap-template-request-dialog-close=${ event => {
						// Check if the dialog reference exists
						if ( ! isDefined( this.$dialog.value ) ) {
							return;
						}

						this.$dialog.value.close();
					} }

					@uap-dialog-close=${ this.handleClose }
				>
					<uap-library-template
						template-id=${ this.templateID }
					></uap-library-template>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Adds the required paraemeters to the URL and creates the dialog nodes
	 */
	handleOpen() {
		// Add parameters to the URL to render the dialog in the page is reloaded
		addParametersToURL(
			window.location.href,
			{
				// eslint-disable-next-line camelcase
				template_id: this.templateID,
			},
			{
				setAsURL: true,
				refresh: false
			}
		);

		this.createDialog();
	}

	/**
	 * Defines what happens when the dialog is closed
	 */
	handleClose() {
		// Remove the parameters
		addParametersToURL(
			window.location.href,
			{
				// eslint-disable-next-line camelcase
				template_id: ''
			},
			{
				setAsURL: true,
				refresh: false
			}
		);
	}

	/**
	 * Checks if the dialog should be opened automatically
	 */
	handleOpenAutomatically() {
		// Get the template ID
		const templateID = parseInt( getURLParameter( 'template_id' ) );

		// Check if it matches the data of this button
		if ( templateID !== this.templateID ) {
			return;
		}

		// If the data matches, we can assume the user is asking to open this dialog automatically
		this.createDialog();
	}

	/**
	 * Retrieves the data for the template directory.
	 *
	 * @return {Object} The directory data object for the current template ID.
	 */
	get directoryData() {
		return getTemplate( this.templateID );
	}
}
